import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import { useCallback, useEffect, useState } from "react";
import { formatEther, isAddress, parseEther, zeroAddress } from "viem";
import { publicActionsL2 } from "viem/op-stack";

import usePublicClient from "src/hooks/usePublicClient";

// Hard shutdown deadline. After this date funds left in the embedded wallet are lost.
const WITHDRAW_DEADLINE = "September 1st, 2026";

export default function ShutdownPage() {
    const { ready, authenticated, user, login, logout, exportWallet, sendTransaction } =
        usePrivy();
    const { wallets } = useWallets();
    const publicClient = usePublicClient();

    const embeddedWallet = wallets.find(
        (wallet) => wallet.walletClientType === "privy",
    );
    const address = embeddedWallet?.address;
    const hasEmbeddedWallet = Boolean(embeddedWallet);

    const [balanceWei, setBalanceWei] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [toAddress, setToAddress] = useState("");
    const [amount, setAmount] = useState("");
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: "success" | "error", message }

    const fetchBalance = useCallback(async () => {
        if (!address) return;
        setBalanceLoading(true);
        try {
            const balance = await publicClient.getBalance({ address });
            setBalanceWei(balance);
        } catch (e) {
            console.error(`Cannot get balance from ${address}`, e);
        } finally {
            setBalanceLoading(false);
        }
    }, [address, publicClient]);

    useEffect(() => {
        fetchBalance();
    }, [fetchBalance]);

    const balanceEth = balanceWei !== null ? formatEther(balanceWei) : null;
    const explorerBaseUrl = publicClient?.chain?.blockExplorers?.default?.url?.replace(
        /\/$/,
        "",
    );

    const copyAddress = async () => {
        try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            console.error("Could not copy address", e);
        }
    };

    const setMaxAmount = async () => {
        if (balanceWei === null || balanceWei === 0n) return;
        try {
            const to = isAddress(toAddress) ? toAddress : zeroAddress;
            const gas = await publicClient.estimateGas({
                account: address,
                to,
                value: balanceWei,
            });
            const gasPrice = await publicClient.getGasPrice();
            const l2Fee = gas * gasPrice;

            // Base is an OP-stack L2: total tx cost includes an L1 data fee that
            // estimateGas does NOT cover. Ignoring it causes "insufficient funds
            // for gas" when sending the max. Add it (best-effort; on non-OP
            // chains like local hardhat this throws and L1 fee stays 0).
            let l1Fee = 0n;
            try {
                const l2Client = publicClient.extend(publicActionsL2());
                l1Fee = await l2Client.estimateL1Fee({
                    account: address,
                    to,
                    value: balanceWei,
                });
            } catch {
                // not an OP-stack chain — no separate L1 fee
            }

            // Generous buffer so a gas-price bump between estimate and send-time
            // can't tip it into failure: 2x the L2 fee + 1.5x the L1 fee.
            const feeBuffer = 2n * l2Fee + l1Fee + l1Fee / 2n;
            const maxWei = balanceWei > feeBuffer ? balanceWei - feeBuffer : 0n;
            setAmount(formatEther(maxWei));
        } catch (e) {
            console.error("Could not estimate gas", e);
            setFeedback({
                type: "error",
                message: "Could not estimate gas. Try a manual amount.",
            });
        }
    };

    const withdraw = async () => {
        setFeedback(null);
        if (!isAddress(toAddress)) {
            setFeedback({
                type: "error",
                message: "Enter a valid destination wallet address.",
            });
            return;
        }
        let value;
        try {
            value = parseEther(amount);
        } catch {
            setFeedback({ type: "error", message: "Enter a valid amount." });
            return;
        }
        if (value <= 0n) {
            setFeedback({
                type: "error",
                message: "Amount must be greater than 0.",
            });
            return;
        }
        setSending(true);
        try {
            const { hash } = await sendTransaction({ to: toAddress, value });
            setFeedback({
                type: "success",
                message: "Transfer sent.",
                hash,
            });
            setWithdrawOpen(false);
            setToAddress("");
            setAmount("");
            fetchBalance();
        } catch (e) {
            console.error(e);
            setFeedback({ type: "error", message: `${e?.message || e}` });
        } finally {
            setSending(false);
        }
    };

    const isReady = ready;
    const isLoggedIn = ready && authenticated && hasEmbeddedWallet;

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                bgcolor: "background.default",
                display: "flex",
                justifyContent: "center",
                px: 2,
                py: 6,
            }}
        >
            <Stack
                spacing={4}
                sx={{ width: "100%", maxWidth: 480, alignItems: "center" }}
            >
                <Box
                    component="img"
                    src="/img/iconmain-192x192.png"
                    alt="Murmur"
                    sx={{ width: 72, height: 72, borderRadius: 2 }}
                />

                <Stack spacing={1.5} sx={{ textAlign: "center" }}>
                    <Typography variant="h4" fontWeight={700}>
                        Murmur is shutting down
                    </Typography>
                    <Typography color="secondary">
                        Thank you for being part of Murmur. The service is being
                        wound down.
                    </Typography>
                    <Typography color="secondary">
                        Murmur's chapter closes today, but the adventure
                        continues, bigger and bolder. Keep watching{" "}
                        <Box
                            component="a"
                            href="https://freatic.team"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: "primary.light" }}
                        >
                            https://freatic.team
                        </Box>
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                        Withdraw any funds from your wallet by{" "}
                        <Box component="span" sx={{ color: "primary.light" }}>
                            {WITHDRAW_DEADLINE}
                        </Box>
                        . After this date, any remaining funds will be lost.
                    </Typography>
                </Stack>

                <Divider flexItem sx={{ borderColor: "background.hairline" }} />

                {!isReady ? (
                    <CircularProgress />
                ) : !isLoggedIn ? (
                    <Stack spacing={2} sx={{ width: "100%", alignItems: "center" }}>
                        <Typography color="secondary" sx={{ textAlign: "center" }}>
                            Log in to access your wallet and withdraw your funds.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={login}
                        >
                            Log in
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={3} sx={{ width: "100%" }}>
                        <Stack
                            spacing={1}
                            sx={{
                                bgcolor: "background.light",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography variant="caption" color="secondary">
                                Your wallet (Base)
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={1}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "monospace",
                                        wordBreak: "break-all",
                                        display: "flex",
                                        alignItems: "center",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {address}
                                </Typography>
                                <Button
                                    size="small"
                                    color="secondary"
                                    startIcon={<ContentCopyIcon fontSize="small" />}
                                    onClick={copyAddress}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {copied ? "Copied" : "Copy"}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack
                            spacing={0.5}
                            sx={{
                                bgcolor: "background.light",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography variant="caption" color="secondary">
                                Balance
                            </Typography>
                            {balanceLoading && balanceEth === null ? (
                                <CircularProgress size={20} />
                            ) : (
                                <Typography variant="h5" fontWeight={700}>
                                    {balanceEth !== null ? balanceEth : "?"} ETH
                                </Typography>
                            )}
                        </Stack>

                        {feedback ? (
                            <Typography
                                sx={{
                                    color:
                                        feedback.type === "success"
                                            ? "primary.light"
                                            : "error.main",
                                    wordBreak: "break-all",
                                }}
                            >
                                {feedback.message}
                                {feedback.hash ? (
                                    <>
                                        <br />
                                        {explorerBaseUrl ? (
                                            <Box
                                                component="a"
                                                href={`${explorerBaseUrl}/tx/${feedback.hash}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: "primary.light",
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                {feedback.hash}
                                            </Box>
                                        ) : (
                                            feedback.hash
                                        )}
                                    </>
                                ) : null}
                            </Typography>
                        ) : null}

                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={!balanceWei}
                            onClick={() => {
                                setFeedback(null);
                                setWithdrawOpen(true);
                            }}
                        >
                            Withdraw funds
                        </Button>

                        <Stack spacing={0.5}>
                            <Button
                                color="secondary"
                                onClick={() => exportWallet({ address })}
                                disabled={!hasEmbeddedWallet}
                            >
                                Export your private key
                            </Button>
                            <Button color="error" onClick={() => logout()}>
                                Log out
                            </Button>
                        </Stack>
                    </Stack>
                )}

                <Typography
                    variant="caption"
                    color="secondary"
                    sx={{ textAlign: "center" }}
                >
                    Questions? Contact{" "}
                    <Box
                        component="a"
                        href="mailto:team@murmurprotocol.com"
                        sx={{ color: "primary.light" }}
                    >
                        team@murmurprotocol.com
                    </Box>
                </Typography>
            </Stack>

            <Dialog
                open={withdrawOpen}
                onClose={() => setWithdrawOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Withdraw funds</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography color="secondary">
                            Send your ETH on Base to another wallet address.
                        </Typography>
                        <TextField
                            label="Destination address"
                            placeholder="0x..."
                            fullWidth
                            value={toAddress}
                            onChange={(e) => setToAddress(e.target.value.trim())}
                        />
                        <TextField
                            label="Amount (ETH)"
                            fullWidth
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <Button
                                            size="small"
                                            onClick={setMaxAmount}
                                            disabled={!balanceWei}
                                        >
                                            Max
                                        </Button>
                                    ),
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            color="secondary"
                            sx={{ textAlign: "right" }}
                        >
                            Balance: {balanceEth !== null ? balanceEth : "?"} ETH
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={withdraw}
                            disabled={sending || !toAddress || !amount}
                            startIcon={
                                sending ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : null
                            }
                        >
                            {sending ? "Sending..." : "Complete transfer"}
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
